// Public, password-gated page (not real auth — just a shared page password,
// per how this was asked for) at /transfer-letter. Reimplements
// reference/CarePro_Wage_Calculator2.2.xlsx's wage logic in JS and produces
// a .docx matching reference/Personalized Wage Chart for Care Pros
// 08312026.docx (same fonts/colors/table styling, extracted from that
// file's own XML) via the `docx` package, downloaded client-side.
import React, { useMemo, useState } from 'react';
import {
  Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, ImageRun,
  ExternalHyperlink, AlignmentType, WidthType, BorderStyle, LineRuleType, HeightRule,
} from 'docx';
import { Lock } from 'lucide-react';

const PAGE_PASSWORD = 'Hisc655';
const SESSION_KEY = 'transfer_letter_authed';

// ── Wage calculation (mirrors the "Wage Calculator" / "Wage Table" sheets) ──

// Wage Table sheet rows 2-16: Min Hours, C/HH base, PCA base, CNA/LPN base.
const HOURS_TIERS = [
  { min: 0, label: '1 - 1,999', chh: 17, pca: 18, cnalpn: 19 },
  { min: 2000, label: '2,000 - 3,999', chh: 17.5, pca: 18.5, cnalpn: 19.5 },
  { min: 4000, label: '4,000 - 5,999', chh: 18, pca: 19, cnalpn: 20 },
  { min: 6000, label: '6,000 - 7,999', chh: 18.5, pca: 19.5, cnalpn: 20.5 },
  { min: 8000, label: '8,000 - 9,999', chh: 19, pca: 20, cnalpn: 21 },
  { min: 10000, label: '10,000 - 11,999', chh: 19.5, pca: 20.5, cnalpn: 21.5 },
  { min: 12000, label: '12,000 - 13,999', chh: 20, pca: 21, cnalpn: 22 },
  { min: 14000, label: '14,000 - 15,999', chh: 20.5, pca: 21.5, cnalpn: 22.5 },
  { min: 16000, label: '16,000 - 17,999', chh: 21, pca: 22, cnalpn: 23 },
  { min: 18000, label: '18,000 - 19,999', chh: 21.5, pca: 22.5, cnalpn: 23.5 },
  { min: 20000, label: '20,000 - 22,499', chh: 22, pca: 23, cnalpn: 24 },
  { min: 22500, label: '22,500 - 24,999', chh: 22.5, pca: 23.5, cnalpn: 24.5 },
  { min: 25000, label: '25,000 - 27,499', chh: 23, pca: 24, cnalpn: 25 },
  { min: 27500, label: '27,500 - 29,999', chh: 23.5, pca: 24.5, cnalpn: 25.5 },
  { min: 30000, label: '30,000+', chh: 24, pca: 25, cnalpn: 26 },
];

// Wage Table sheet rows 2-9 (J:L columns) — the eight enterable profile
// rates, which "base service" group they belong to, and their shift offset.
const ANCHOR_PROFILES = {
  'C/HH': { service: 'C/HH', offset: 0 },
  'C/HH - Weekend': { service: 'C/HH', offset: 1 },
  'C/HH - Couple': { service: 'C/HH', offset: 3 },
  'C/HH - Couple - Weekend': { service: 'C/HH', offset: 4 },
  'PC': { service: 'PC', offset: 0 },
  'PC - Weekend': { service: 'PC', offset: 1 },
  'PC - Couple': { service: 'PC', offset: 3 },
  'PC - Couple - Weekend': { service: 'PC', offset: 4 },
};
const ANCHOR_PROFILE_OPTIONS = Object.keys(ANCHOR_PROFILES);

const HIGHEST_SERVICE_OPTIONS = ['Companionship/Home Helper', 'Personal Care (PCA)', 'CNA/LPN'];

// Requirements that only accept Yes/No in the sheet's own formula (no "N/A"
// branch) vs. the five that accept Yes or N/A.
const YES_ONLY_REQUIREMENTS = [
  { key: 'alzheimers', label: "HI Alzheimer's Training Course" },
  { key: 'annualRefresher', label: 'Most Recent Mandatory Annual Refresher' },
  { key: 'ceus', label: '12 CEUs in the Last 12 Months' },
];
const YES_OR_NA_REQUIREMENTS = [
  { key: 'driversLicense', label: "Current Driver's License" },
  { key: 'carInsurance', label: 'Current Car Insurance' },
  { key: 'carRegistration', label: 'Current Car Registration' },
  { key: 'tbTest', label: 'Current TB Test' },
  { key: 'cnaLicense', label: 'Current CNA/LPN License' },
];

const yesOrNA = (v) => v === 'Yes' || v === 'N/A';

const tier2RequirementsMet = (reqs, highestService) =>
  reqs.alzheimers === 'Yes' &&
  reqs.annualRefresher === 'Yes' &&
  yesOrNA(reqs.driversLicense) &&
  yesOrNA(reqs.carInsurance) &&
  yesOrNA(reqs.carRegistration) &&
  yesOrNA(reqs.tbTest) &&
  (highestService === 'CNA/LPN' ? reqs.cnaLicense === 'Yes' : yesOrNA(reqs.cnaLicense)) &&
  reqs.ceus === 'Yes';

// MATCH(hours, mins, 1) — largest tier whose min <= hours (tiers are sorted).
const hoursTierIndex = (hours) => {
  if (!(hours >= 0)) return 0;
  let idx = 0;
  for (let i = 0; i < HOURS_TIERS.length; i++) {
    if (HOURS_TIERS[i].min <= hours) idx = i; else break;
  }
  return idx;
};

const eligibleTierIndex = (hours, tier2Met) => {
  if (!(hours >= 0) || hours < 2000) return 0;
  return tier2Met ? hoursTierIndex(hours) : 0;
};

// Returns all 8 profile rates (each flagged `active`) plus the tier/rate-
// source context shown in the live preview.
function computeWageRates({ hours, highestService, rateSource, anchorProfile, anchorRateInput, reqs }) {
  const numHours = hours === '' ? NaN : Number(hours);
  const tier2Met = tier2RequirementsMet(reqs, highestService);
  const eligibleIdx = eligibleTierIndex(numHours, tier2Met);
  const tier = HOURS_TIERS[eligibleIdx];
  const policyChh = tier.chh;

  const anchorRate = anchorRateInput === '' ? NaN : Number(anchorRateInput);
  let activeChhBase = policyChh;
  let rateSourceNote = 'Policy wage table';
  if (rateSource === 'Manual anchor rate') {
    if (Number.isNaN(anchorRate)) {
      rateSourceNote = 'Manual mode — enter anchor rate (using policy rate until then)';
    } else {
      const anchor = ANCHOR_PROFILES[anchorProfile];
      const anchorIsPcOnlyOnCHHEmployee = anchor.service === 'PC' && highestService === 'Companionship/Home Helper';
      if (anchorIsPcOnlyOnCHHEmployee) {
        rateSourceNote = `${anchorProfile} anchor requires PCA/CNA qualification — using policy rate instead`;
      } else {
        const servicePremium = anchor.service === 'PC' ? (highestService === 'CNA/LPN' ? 2 : 1) : 0;
        activeChhBase = anchorRate - anchor.offset - servicePremium;
        rateSourceNote = `Manual: ${anchorProfile} = $${anchorRate.toFixed(2)}`;
      }
    }
  }

  const pcActive = highestService === 'Personal Care (PCA)' || highestService === 'CNA/LPN';
  const activePcBase = highestService === 'Personal Care (PCA)' ? activeChhBase + 1
    : highestService === 'CNA/LPN' ? activeChhBase + 2 : 0;

  const rates = [
    { key: 'pc', label: 'PC', value: pcActive ? activePcBase : 0, active: pcActive },
    { key: 'pc_weekend', label: 'PC - Weekend', value: pcActive ? activePcBase + 1 : 0, active: pcActive },
    { key: 'pc_couple', label: 'PC - Couple', value: pcActive ? activePcBase + 3 : 0, active: pcActive },
    { key: 'pc_couple_weekend', label: 'PC - Couple - Weekend', value: pcActive ? activePcBase + 4 : 0, active: pcActive },
    { key: 'chh', label: 'C/HH', value: activeChhBase, active: true },
    { key: 'chh_weekend', label: 'C/HH - Weekend', value: activeChhBase + 1, active: true },
    { key: 'chh_couple', label: 'C/HH - Couple', value: activeChhBase + 3, active: true },
    { key: 'chh_couple_weekend', label: 'C/HH - Couple - Weekend', value: activeChhBase + 4, active: true },
  ];

  return {
    rates,
    tierLabel: tier.label,
    tier2Met,
    hoursQualify: numHours >= 2000,
    rateSourceNote,
  };
}

const money = (n) => `$${n.toFixed(2)}`;

// ── .docx generation (matches the reference file's own fonts/colors/borders) ──

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
const NO_BORDERS = { top: NO_BORDER, bottom: NO_BORDER, left: NO_BORDER, right: NO_BORDER };

// Fixed per the reference letter — not exposed as form fields.
const SIGNER_NAME = 'Jack St.Clair';
const SIGNER_TITLE = 'Franchise Owner';
const CONTACT_EMAIL = 'jack.stclair@homeinstead.com';
const CONTACT_PHONE = '703-464-0035, extension 9';

async function buildTransferLetterBlob({ careProName, letterDateLabel, startDateLabel, rates }) {
  const logoResp = await fetch('/transfer-letter-logo.png');
  const logoBuffer = await logoResp.arrayBuffer();

  const tableRows = rates.map((r, i) => {
    const fill = i % 2 === 0 ? 'FFFFFF' : 'F8FAFB';
    return new TableRow({
      height: { value: 460, rule: HeightRule.ATLEAST },
      children: [
        new TableCell({
          width: { size: 4440, type: WidthType.DXA },
          shading: { fill },
          borders: NO_BORDERS,
          children: [new Paragraph({ children: [new TextRun({ text: r.label, font: 'Aptos', size: 18, color: '1F2937' })] })],
        }),
        new TableCell({
          width: { size: 1560, type: WidthType.DXA },
          shading: { fill },
          borders: NO_BORDERS,
          children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: money(r.value), font: 'Aptos', size: 22, bold: true, color: '154E7A' })] })],
        }),
      ],
    });
  });

  const g = (text) => new TextRun({ text, font: 'Garamond' });

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: 'Garamond', size: 24 },
          paragraph: { spacing: { after: 160, line: 278, lineRule: LineRuleType.AUTO } },
        },
      },
    },
    sections: [{
      properties: {
        page: {
          size: { width: 12240, height: 15840 },
          margin: { top: 549, bottom: 1440, left: 1440, right: 1440 },
        },
      },
      children: [
        new Paragraph({ alignment: AlignmentType.CENTER, children: [new ImageRun({ data: logoBuffer, transformation: { width: 161, height: 81 } })] }),
        new Paragraph({ alignment: AlignmentType.CENTER, children: [] }),
        new Paragraph({ children: [g(letterDateLabel)] }),
        new Paragraph({ children: [g(`Dear ${careProName},`)] }),
        new Paragraph({ children: [g(`Welcome to your local Home Instead team!  We look forward to you starting back with our company officially on ${startDateLabel}.`)] }),
        new Paragraph({ children: [g('Here is your personalized wage chart:')] }),
        new Table({ width: { size: 6000, type: WidthType.DXA }, rows: tableRows }),
        new Paragraph({ children: [] }),
        new Paragraph({
          children: [
            g('If you have questions about this transition, please email '),
            new ExternalHyperlink({ link: `mailto:${CONTACT_EMAIL}`, children: [new TextRun({ text: CONTACT_EMAIL, font: 'Garamond', style: 'Hyperlink' })] }),
            g(` or call ${CONTACT_PHONE}. `),
          ],
        }),
        new Paragraph({ children: [g('With appreciation,')] }),
        new Paragraph({ children: [g(SIGNER_NAME)] }),
        new Paragraph({ children: [g(SIGNER_TITLE)] }),
      ],
    }],
  });

  return Packer.toBlob(doc);
}

const longDate = (isoDate) => {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ── UI ──

const fieldCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2c4f26]/30 focus:border-[#2c4f26]';
const labelCls = 'block text-xs font-semibold text-gray-600 mb-1';

const PasswordGate = ({ onUnlock }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const submit = (e) => {
    e.preventDefault();
    if (value === PAGE_PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, 'true');
      onUnlock();
    } else {
      setError('Incorrect password');
    }
  };
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <form onSubmit={submit} className="bg-white border border-gray-200 rounded-2xl p-8 max-w-sm w-full">
        <div className="w-12 h-12 bg-[#f0f5f0] rounded-full flex items-center justify-center mb-4">
          <Lock size={20} className="text-[#2c4f26]" />
        </div>
        <h1 className="text-lg font-bold text-gray-900 mb-1">Transfer Letter</h1>
        <p className="text-sm text-gray-600 mb-4">Enter the page password to continue.</p>
        <input
          type="password"
          autoFocus
          value={value}
          onChange={(e) => { setValue(e.target.value); setError(''); }}
          className={fieldCls}
          placeholder="Password"
        />
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        <button type="submit" className="mt-4 w-full bg-[#2c4f26] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#3a6b32] transition">
          Unlock
        </button>
      </form>
    </div>
  );
};

const TransferLetterForm = () => {
  const [careProName, setCareProName] = useState('');
  const [startDate, setStartDate] = useState('2026-09-29');
  const [letterDate, setLetterDate] = useState(todayIso());
  const [hours, setHours] = useState('');
  const [highestService, setHighestService] = useState('Personal Care (PCA)');
  const [rateSource, setRateSource] = useState('Manual anchor rate');
  const [anchorProfile, setAnchorProfile] = useState('C/HH');
  const [anchorRateInput, setAnchorRateInput] = useState('');
  const [reqs, setReqs] = useState({
    alzheimers: 'Yes', annualRefresher: 'Yes', ceus: 'Yes',
    driversLicense: 'Yes', carInsurance: 'Yes', carRegistration: 'Yes', tbTest: 'Yes', cnaLicense: 'Yes',
  });
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');

  const setReq = (key, val) => setReqs((prev) => ({ ...prev, [key]: val }));

  const result = useMemo(() => computeWageRates({ hours, highestService, rateSource, anchorProfile, anchorRateInput, reqs }), [hours, highestService, rateSource, anchorProfile, anchorRateInput, reqs]);

  const canGenerate = careProName.trim() && startDate;

  const generate = async () => {
    setGenError('');
    setGenerating(true);
    try {
      const blob = await buildTransferLetterBlob({
        careProName: careProName.trim(),
        letterDateLabel: longDate(letterDate),
        startDateLabel: longDate(startDate),
        rates: result.rates,
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const safeName = careProName.trim().replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '') || 'care-pro';
      a.download = `Personalized Wage Chart - ${safeName}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('Transfer letter generation failed:', err);
      setGenError('Failed to generate the document. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="h-screen bg-gray-50 py-4 px-4 overflow-hidden flex flex-col">
      <div className="max-w-6xl w-full mx-auto flex flex-col min-h-0 flex-1">
        <div className="mb-3 shrink-0">
          <h1 className="text-lg font-bold text-gray-900">Care Pro Transfer Letter</h1>
          <p className="text-xs text-gray-600">Enter the care pro's details to calculate their wage chart and generate the personalized transfer letter.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 min-h-0 flex-1">
          <div className="lg:col-span-3 flex flex-col gap-3 min-h-0">
            <div className="bg-white border border-gray-200 rounded-xl p-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                <div>
                  <label className={labelCls}>Care Pro Name</label>
                  <input className={fieldCls} value={careProName} onChange={(e) => setCareProName(e.target.value)} placeholder="Jane Doe" />
                </div>
                <div>
                  <label className={labelCls}>Official Start Date</label>
                  <input type="date" className={fieldCls} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Letter Date</label>
                  <input type="date" className={fieldCls} value={letterDate} onChange={(e) => setLetterDate(e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Cumulative Hours Worked</label>
                  <input type="number" min="0" className={fieldCls} value={hours} onChange={(e) => setHours(e.target.value)} placeholder="0" />
                </div>
                <div>
                  <label className={labelCls}>Highest Approved Service</label>
                  <select className={fieldCls} value={highestService} onChange={(e) => setHighestService(e.target.value)}>
                    {HIGHEST_SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Rate Source</label>
                  <select className={fieldCls} value={rateSource} onChange={(e) => setRateSource(e.target.value)}>
                    <option value="Manual anchor rate">Manual anchor rate</option>
                    <option value="Policy wage table">Policy wage table</option>
                  </select>
                </div>
                {rateSource === 'Manual anchor rate' && (
                  <>
                    <div>
                      <label className={labelCls}>Anchor Profile Rate</label>
                      <select className={fieldCls} value={anchorProfile} onChange={(e) => setAnchorProfile(e.target.value)}>
                        {ANCHOR_PROFILE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Entered Anchor Rate ($/hr)</label>
                      <input type="number" step="0.25" min="0" className={fieldCls} value={anchorRateInput} onChange={(e) => setAnchorRateInput(e.target.value)} placeholder="17.00" />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-3">
              <h2 className="text-xs font-bold text-gray-900 mb-2">Tier 2+ Requirements</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {[...YES_ONLY_REQUIREMENTS, ...YES_OR_NA_REQUIREMENTS].map((r) => (
                  <div key={r.key}>
                    <label className={labelCls}>{r.label}</label>
                    <select className={fieldCls} value={reqs[r.key]} onChange={(e) => setReq(r.key, e.target.value)}>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      {YES_OR_NA_REQUIREMENTS.some((x) => x.key === r.key) && <option value="N/A">N/A</option>}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {genError && <p className="text-xs text-red-600">{genError}</p>}
            <button
              type="button"
              onClick={generate}
              disabled={!canGenerate || generating}
              className="w-full sm:w-auto bg-[#2c4f26] text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#3a6b32] disabled:opacity-50 transition"
            >
              {generating ? 'Generating…' : 'Generate Word Document'}
            </button>
            {!canGenerate && <p className="text-xs text-gray-500">Enter the care pro's name and official start date to generate the letter.</p>}
          </div>

          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-3 flex flex-col min-h-0">
            <h2 className="text-xs font-bold text-gray-900 mb-1">Wage Chart Preview</h2>
            <p className="text-[11px] text-gray-600 mb-2">
              Tier: <span className="font-medium text-gray-800">{result.tierLabel}</span>
              {' · '}Tier 2+ met: <span className="font-medium text-gray-800">{result.tier2Met ? 'Yes' : 'No'}</span>
              <br />{result.rateSourceNote}
            </p>
            <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden overflow-y-auto">
              {result.rates.map((r, i) => (
                <div key={r.key} className={`flex items-center justify-between px-3 py-1.5 text-sm ${i % 2 === 1 ? 'bg-gray-50' : ''}`}>
                  <span className="text-gray-700">
                    {r.label}
                    {!r.active && <span className="ml-2 text-[11px] text-gray-400">Not PC-qualified</span>}
                  </span>
                  <span className="font-semibold text-[#154E7A]">{money(r.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TransferLetterPage = () => {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(SESSION_KEY) === 'true');
  if (!authed) return <PasswordGate onUnlock={() => setAuthed(true)} />;
  return <TransferLetterForm />;
};

export default TransferLetterPage;
